import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { MapProvider } from '../map/map-provider';
import { ScaleBarState } from './scale-bar-state';
import * as reactiveUtils from '@arcgis/core/core/reactiveUtils.js';

interface Division {
  value: number;
  label: string;
  pixels: number;
}

@Injectable()
export class ScaleBarNotifier extends BaseNotifier<ScaleBarState> {
  private static readonly INITIAL: ScaleBarState = {
    scale: null,
    unit: 'metric',
    divisionLabels: [],
    unitLabel: 'km',
    ratio: '',
  };

  constructor(ref: Ref) {
    super(ref, ScaleBarNotifier.INITIAL);
  }

  override build(): void {
    const tryWatch = () => {
      const v = this.ref.watch(MapProvider).state().view;
      if (!v) return false;
      const update = () => {
        this.updateState(s => ({ ...s, scale: v.scale }));
        this.computeDivisions();
      };
      update();
      this.trackHandle(reactiveUtils.watch(() => v.scale, () => update()));
      return true;
    };
    if (!tryWatch()) {
      const iv = setInterval(() => { if (tryWatch()) clearInterval(iv); }, 500);
      this.trackDisposable(() => clearInterval(iv));
    }
  }

  cycleUnit(): void {
    const current = this.snapshot().unit;
    const next = current === 'metric' ? 'imperial' : 'metric';
    this.setState({ ...this.snapshot(), unit: next });
    this.computeDivisions();
  }

  private computeDivisions(): void {
    const s = this.snapshot();
    if (!s.scale) return;
    const isMetric = s.unit === 'metric';
    const d = this.calculateDivisions(s.scale, isMetric);
    this.updateState(state => ({
      ...state,
      divisionLabels: d.map(x => x.label),
      unitLabel: isMetric ? 'km' : 'mi',
      ratio: `1:${Math.round(s.scale ?? 0).toLocaleString()}`,
    }));
  }

  private calculateDivisions(scale: number, metric: boolean): Division[] {
    const dpi = 96;
    const inchesPerMeter = 39.37;
    const groundRes = scale / (dpi * inchesPerMeter);
    const metersPerPixel = metric ? groundRes : groundRes * 1609.34;

    const niceNumbers = metric
      ? [100000000, 50000000, 20000000, 10000000, 5000000, 2000000, 1000000, 500000, 200000, 100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1]
      : [100000000, 50000000, 20000000, 10000000, 5000000, 2000000, 1000000, 500000, 200000, 100000, 50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100, 50, 20, 10, 5, 2, 1];

    const base = metric ? 1 : 1609.34;
    const pixelLen = 200;
    const targetGround = pixelLen * metersPerPixel * base;

    let division = niceNumbers.find(n => targetGround / n >= 1) ?? niceNumbers[niceNumbers.length - 1];
    if (metric) {
      if (division >= 1000) {
        return [{ value: division / 1000, label: `${division / 1000}`, pixels: (division / metersPerPixel) * (pixelLen / targetGround) }];
      }
      return [{ value: division, label: `${division}`, pixels: (division / metersPerPixel) * (pixelLen / targetGround) }];
    }
    return [{ value: division / 1609.34, label: `${(division / 1609.34).toFixed(1)}`, pixels: (division / metersPerPixel / 1609.34) * (pixelLen / targetGround) }];
  }
}
